import React from 'react';
import { useKeywordAssistantEditor } from '@/contexts/KeywordAssistantEditorContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useBrandData } from '@/hooks/useBrandData';
import { useKeywordAssistant } from '@/hooks/useKeywordAssistant';
import { useOpenAIKeywords } from '@/hooks/useOpenAIKeywords';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { KeywordAssistantResults } from '@/components/keyword-assistant/KeywordAssistantResults';
import { X, Wand2, Search, Play, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

export function GlobalKeywordAssistantEditor() {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  
  const {
    isOpen,
    formState,
    closeEditor,
    resetEditor,
    updateFormField,
  } = useKeywordAssistantEditor();

  // Scroll to top when drawer opens
  React.useEffect(() => {
    if (isOpen && scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [isOpen]);

  const { currentBrandId, currentBrand } = useBrandManager();
  const { brandData } = useBrandData(currentBrandId);
  
  const { 
    suggestions: hookSuggestions, 
    isLoading: isSearchingDataForSEO, 
    searchKeywords, 
    addSelectedKeywords,
    clearSuggestions
  } = useKeywordAssistant(currentBrandId);

  const {
    keywords: hookOpenAIKeywords,
    isLoading: isGeneratingKeywords,
    generateKeywords,
    clearKeywords
  } = useOpenAIKeywords(currentBrandId);

  // Sync hook results to context for persistence
  React.useEffect(() => {
    if (hookOpenAIKeywords.length > 0) {
      updateFormField('openAIKeywords', hookOpenAIKeywords);
    }
  }, [hookOpenAIKeywords, updateFormField]);

  React.useEffect(() => {
    if (hookSuggestions.length > 0) {
      updateFormField('suggestions', hookSuggestions);
    }
  }, [hookSuggestions, updateFormField]);

  const handleGenerateKeywords = async () => {
    if (!formState.topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    if (!currentBrandId) {
      toast.error("Please select a brand first");
      return;
    }

    clearKeywords();
    updateFormField('selectedKeywords', []);
    updateFormField('openAIKeywords', []);
    await generateKeywords(formState.topic);
  };

  const handleKeywordClick = (keyword: string) => {
    const current = formState.selectedKeywords;
    if (current.includes(keyword)) {
      updateFormField('selectedKeywords', current.filter(k => k !== keyword));
    } else {
      updateFormField('selectedKeywords', [...current, keyword]);
    }
  };

  const handleCountryToggle = (countryCode: string) => {
    const current = formState.selectedCountries;
    if (current.includes(countryCode)) {
      updateFormField('selectedCountries', current.filter(c => c !== countryCode));
    } else {
      updateFormField('selectedCountries', [...current, countryCode]);
    }
  };

  const handleSelectAllCountries = () => {
    if (brandData.targetedCountries) {
      updateFormField('selectedCountries', brandData.targetedCountries.map(c => c.code.toString()));
    }
  };

  const handleClearAllCountries = () => {
    updateFormField('selectedCountries', []);
  };

  const handleSelectAllKeywords = () => {
    updateFormField('selectedKeywords', formState.openAIKeywords);
  };

  const handleGoSearch = async () => {
    if (formState.selectedKeywords.length === 0) {
      toast.error("No keywords selected. Please select keywords first.");
      return;
    }
    
    if (formState.selectedCountries.length === 0) {
      toast.error("Please select at least one country");
      return;
    }

    if (!currentBrandId) {
      toast.error("Please select a brand first");
      return;
    }

    clearSuggestions();
    updateFormField('suggestions', []);
    
    for (let i = 0; i < formState.selectedCountries.length; i++) {
      const countryCode = formState.selectedCountries[i];
      updateFormField('researchProgress', { current: i + 1, total: formState.selectedCountries.length });
      await searchKeywords(formState.selectedKeywords, countryCode, true);
    }
    
    updateFormField('researchProgress', null);
  };

  const handleAddSelected = async (selectedKeywordsFromResults: string[]) => {
    if (selectedKeywordsFromResults.length === 0) {
      toast.error("Please select keywords to add");
      return;
    }
    await addSelectedKeywords(selectedKeywordsFromResults);
  };

  const handleReset = () => {
    clearKeywords();
    clearSuggestions();
    resetEditor();
  };

  // Use context state or hook state (context takes precedence for persistence)
  const displayOpenAIKeywords = formState.openAIKeywords.length > 0 ? formState.openAIKeywords : hookOpenAIKeywords;
  const displaySuggestions = formState.suggestions.length > 0 ? formState.suggestions : hookSuggestions;

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && closeEditor()} direction="right">
      <DrawerContent className="h-full w-full max-w-none inset-y-0 right-0 left-auto rounded-none mt-0 fixed">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <Wand2 className="h-6 w-6 text-primary" />
              <div>
                <h2 className="text-xl font-semibold">Keyword Assistant</h2>
                <p className="text-sm text-muted-foreground">
                  Generate keywords with AI, then research their metrics
                  {currentBrand && (
                    <span className="ml-2 text-primary">({currentBrand.name})</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleReset}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button variant="ghost" size="icon" onClick={closeEditor}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <ScrollArea ref={scrollAreaRef} className="flex-1 px-6 py-4">
            <div className="space-y-6 max-w-4xl mx-auto">
              {/* Step 1: Generate Keywords with OpenAI */}
              <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wand2 className="w-5 h-5" />
                    Step 1: Generate Keywords with AI
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="topic">Topic</Label>
                    <Input
                      id="topic"
                      value={formState.topic}
                      onChange={(e) => updateFormField('topic', e.target.value)}
                      placeholder="e.g., car rental, travel insurance"
                      className="mt-2"
                    />
                  </div>

                  <Button 
                    onClick={handleGenerateKeywords}
                    disabled={isGeneratingKeywords || !formState.topic.trim() || !currentBrandId}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    {isGeneratingKeywords ? "Generating..." : "Generate Keywords with AI"}
                  </Button>

                  {displayOpenAIKeywords.length > 0 && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-green-700 font-medium">
                          Generated {displayOpenAIKeywords.length} keywords - Click to select:
                        </p>
                        <Button 
                          onClick={handleSelectAllKeywords}
                          variant="outline"
                          size="sm"
                          disabled={formState.selectedKeywords.length === displayOpenAIKeywords.length}
                          className="text-xs"
                        >
                          Select All
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                        {displayOpenAIKeywords.map((keyword, index) => (
                          <Badge 
                            key={index} 
                            variant={formState.selectedKeywords.includes(keyword) ? "default" : "secondary"} 
                            className={`text-xs cursor-pointer transition-colors ${
                              formState.selectedKeywords.includes(keyword) 
                                ? "bg-purple-600 hover:bg-purple-700 text-white" 
                                : "hover:bg-gray-200"
                            }`}
                            onClick={() => handleKeywordClick(keyword)}
                          >
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                      {formState.selectedKeywords.length > 0 && (
                        <div className="mt-3 p-3 bg-purple-50 rounded-md">
                          <p className="text-sm text-purple-700 font-medium mb-2">
                            Selected {formState.selectedKeywords.length} keyword(s):
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {formState.selectedKeywords.map((keyword, index) => (
                              <Badge key={index} className="text-xs bg-purple-600 text-white">
                                {keyword}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!currentBrandId && (
                    <p className="text-sm text-red-600 text-center">
                      No brand selected. Please select a brand first.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Step 2: Research Keywords with DataForSEO */}
              {formState.selectedKeywords.length > 0 && (
                <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Search className="w-5 h-5" />
                      Step 2: Research Keyword Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Countries (select one or more)</Label>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={handleSelectAllCountries}
                            disabled={formState.selectedCountries.length === brandData.targetedCountries?.length}
                            className="text-xs"
                          >
                            Select All
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={handleClearAllCountries}
                            disabled={formState.selectedCountries.length === 0}
                            className="text-xs"
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                      
                      <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                        {brandData.targetedCountries?.map((country) => (
                          <div 
                            key={country.code} 
                            className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-1.5 rounded"
                            onClick={() => handleCountryToggle(country.code.toString())}
                          >
                            <Checkbox 
                              checked={formState.selectedCountries.includes(country.code.toString())}
                              onCheckedChange={() => handleCountryToggle(country.code.toString())}
                            />
                            <span className="text-sm">{country.name}</span>
                          </div>
                        ))}
                      </div>

                      {formState.selectedCountries.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {formState.selectedCountries.map(code => {
                            const country = brandData.targetedCountries?.find(c => c.code.toString() === code);
                            return country ? (
                              <Badge key={code} variant="secondary" className="text-xs">
                                {country.name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>

                    <Button 
                      onClick={handleGoSearch}
                      disabled={isSearchingDataForSEO || formState.selectedCountries.length === 0 || formState.selectedKeywords.length === 0}
                      className="w-full bg-teal-600 hover:bg-teal-700"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {isSearchingDataForSEO 
                        ? formState.researchProgress 
                          ? `Researching country ${formState.researchProgress.current} of ${formState.researchProgress.total}...` 
                          : "Researching..."
                        : `GO - Research Keywords in ${formState.selectedCountries.length} ${formState.selectedCountries.length === 1 ? 'Country' : 'Countries'}`
                      }
                    </Button>

                    {brandData.targetedCountries?.length === 0 && currentBrandId && (
                      <p className="text-sm text-orange-600 text-center">
                        No target countries configured. Please add countries in Brand Setup first.
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {displaySuggestions.length > 0 && (
                <KeywordAssistantResults 
                  suggestions={displaySuggestions}
                  onAddSelected={handleAddSelected}
                  isLoading={isSearchingDataForSEO}
                />
              )}
            </div>
          </ScrollArea>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
